import type { ListBuilderConfiguration } from '../../types';
import { allLicensesList } from './lists/all-licenses';
import { atRiskList } from './lists/at-risk';
import { confirmedList } from './lists/confirmed';
import { discrepanciesList } from './lists/discrepancies';
import { expiredList } from './lists/expired';
import { expiringSoonList } from './lists/expiring-soon';
import { missingFilesList } from './lists/missing-files';
import { pendingMatchList } from './lists/pending-match';
import { recentlyChangedList } from './lists/recently-changed';
import { unmatchedList } from './lists/unmatched';

/**
 * License list configuration with compliance workflow tabs.
 *
 * Tab order reflects the compliance officer's workflow:
 * 1. Pending Match - intake queue for unmatched licenses
 * 2. Recently Changed - registry detected a status change in last 7 days
 * 3. Expiring Soon - renewal queue for licenses approaching expiration
 * 4. Expired - licenses that have lapsed
 * 5. At Risk - licenses with an active disciplinary record / suspended / revoked
 * 6. Missing Files - matched licenses with no supporting documentation
 * 7. Under Review - truly unmatched licenses with no candidate match
 * 8. Discrepancies - matched licenses whose stored values disagree with the
 *    registry on the user-visible fields (license number, expiration date)
 * 9. Confirmed - audit trail of confirmed registry matches
 * 10. All Licenses - unfiltered view for ad-hoc searching
 */
export const LICENSE_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		pendingMatchList,
		recentlyChangedList,
		expiringSoonList,
		expiredList,
		atRiskList,
		missingFilesList,
		unmatchedList,
		discrepanciesList,
		confirmedList,
		allLicensesList,
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: [
			'licenseKeywords',
			'licenseStatus',
			'licenseType',
			'licenseState',
			'licenseClient',
			'licenseClientLeadOrCustomer',
			'licenseSalesRep',
			'licenseExpirationDateRange',
			'licenseHasDisciplinary',
			'licenseHasFiles',
			'licenseMatchStatus',
			'licenseMatchConfidence',
			'licenseRecentlyChanged',
			'licenseHasRegistryDiscrepancy',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/licenses/:id',
			objectLabel: 'License',
			objectTable: 'client_license',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'licenseNumber', fieldLabel: 'License #' },
				{ fieldName: 'type', fieldLabel: 'Type' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'expirationDate', fieldLabel: 'Expiration Date' },
				{ fieldName: 'issueDate', fieldLabel: 'Issue Date' },
				{ fieldName: 'issuingState', fieldLabel: 'Issuing State' },
				{ fieldName: 'issuingAuthority', fieldLabel: 'Issuing Authority' },
				{ fieldName: 'hasDisciplinaryAction', fieldLabel: 'Has Disciplinary' },
				{ fieldName: 'fileCount', fieldLabel: 'Files' },
				{ fieldName: 'clientName', fieldLabel: 'Client' },
				{ fieldName: 'clientLeadOrCustomer', fieldLabel: 'Lead/Customer' },
				{ fieldName: 'clientStreetAddress', fieldLabel: 'Street Address' },
				{ fieldName: 'clientCityStateZip', fieldLabel: 'City/State/Zip' },
			],
		},
	],
};
